import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { createEnquiry } from '../services/Enquiry';
import { SEOHead } from './SEOHead';
import { useSiteSettings, getPageSeo, buildFaqSchema } from '../services/SiteSettings';
import { PageFaq } from './PageAeo';
import { buildOrganizationSchema } from '../data/organizationSchema.mjs';

export function ContactPage() {
  const settings = useSiteSettings();
  const { contact } = settings;
  const seo = getPageSeo(settings, '/contact');
  const faqSchema = buildFaqSchema(seo.faqs);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    phone: '',
    organization: '',
    source: 'WEB'
  });

  const [loading, setLoading] = useState(false);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      details: [contact.addressDisplay]
    },
    {
      icon: Phone,
      title: 'Phone',
      details: [contact.phones.join(' / ')]
    },
    {
      icon: Mail,
      title: 'Email',
      details: [contact.email]
    },
    {
      icon: Clock,
      title: 'Office Hours',
      details: contact.officeHours
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);

    try {
      await createEnquiry(formData);
      toast.success("Thank you! Your message has been sent successfully.");
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        phone: '',
        organization: '',
        source: 'WEB'
      });
    } catch (error) {
      toast.error(error.message || "Failed to send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": buildOrganizationSchema(settings)
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://uspconsultancy.com.np/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": "https://uspconsultancy.com.np/contact"
      }
    ]
  };

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Contact Us'}
        description={seo.description || `Contact ${contact.name}. Office: ${contact.streetAddress}, ${contact.locality}. Email: ${contact.email}, Phone: ${contact.phones.join(', ')}.`}
        canonical="https://uspconsultancy.com.np/contact"
        keywords={seo.keywords}
        structuredData={faqSchema ? [contactSchema, breadcrumbSchema, faqSchema] : [contactSchema, breadcrumbSchema]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="mb-6">Contact Us</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We welcome collaborations, partnerships, and inquiries from organizations and professionals who share our commitment to research excellence and social impact.
          </p>
        </div>

        {/* Main Contact Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Send className="w-5 h-5" />
                <span>Send us a Message</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+977-9800000000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="organization">Organization</Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="Your organization"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What is this regarding?"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project or inquiry..."
                    required
                  />
                </div>

                <Button type="submit" className="w-full cursor-pointer" disabled={loading}>
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Additional Information */}
          {/* <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Why Contact Us?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <h4 className="mb-1">Expert Consultation</h4>
                    <p className="text-gray-600 text-sm">Get insights from our experienced team of professionals.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <h4 className="mb-1">Customized Solutions</h4>
                    <p className="text-gray-600 text-sm">Tailored approaches to meet your specific requirements.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <h4 className="mb-1">Quick Response</h4>
                    <p className="text-gray-600 text-sm">We respond to all inquiries within 24 hours.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                  <div>
                    <h4 className="mb-1">No Obligation</h4>
                    <p className="text-gray-600 text-sm">Free initial consultation to discuss your needs.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Service Areas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">We provide services across Nepal and neighboring regions:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>Kathmandu Valley</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>Central Nepal</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>Eastern Nepal</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>Western Nepal</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>Remote consultation available</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div> */}
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => (
            <Card key={index} className="text-center">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="mb-3">{info.title}</h3>
                <div className="space-y-1">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Map Section */}
        <Card className="mb-16">
          <CardHeader>
            <CardTitle>Find Us</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg overflow-hidden h-64">
              <iframe
                title={`${contact.name} Location`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                  `${parseFloat(contact.latitude) || 0},${parseFloat(contact.longitude) || 0}`
                )}&z=16&output=embed`}
              ></iframe>
            </div>
          </CardContent>
        </Card>


        {/* Emergency Contact */}
        {/* <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-red-800 mb-2">Emergency Contact</h3>
              <p className="text-red-700 mb-4">For urgent project matters or emergency consultations:</p>
              <div className="space-y-2">
                <p className="text-red-800">Emergency Hotline: +977 1 4568118</p>
                <p className="text-red-700 text-sm">Available 24/7 for critical project issues</p>
              </div>
            </div>
          </CardContent>
        </Card> */}
      </div>
      <PageFaq faqs={seo.faqs} />
    </div>
  );
}
